// ScopeStep.jsx — scope confirmation, between the two extraction stages.
//
// Fieldset is immutable after creation and everything downstream hangs off it
// (which post-options to fetch, which scoping keys are legal, which existing
// fields to sweep), so the human confirms fieldset + module/type scope HERE,
// before stage 2 runs. The AI's proposals are pre-checked suggestions with
// their rationale shown — nothing is silently guessed.
//
// Scoping pickers are rendered generically from the LIVE
// /v1/custom-fields/post-options response: every systemField whose key names
// an ID association (…ID / …IDs) gets a picker populated from its live
// ID→name options map. Names are stored alongside IDs so saved scopes stay
// readable if FMX renames things later.

import { useEffect, useMemo, useState } from 'react';
import { T } from '../../theme';
import { Button, Badge } from '../fmxkit';
import { FIELDSET_GROUPS, ALL_FIELDSETS, fieldsetLabel } from '../../fieldBuilder/fieldsets';
import { normalizeFieldName } from '../../fieldBuilder/dedupe';

const isScopingKey = (key) => /IDs?$/.test(key);
const isArrayKey = (key) => /IDs$/.test(key);

function ScopingPicker({ sf, value, onChange }) {
  const [filter, setFilter] = useState('');
  const options = Object.entries(sf.options || {}); // [id, name]
  const multi = isArrayKey(sf.key);
  const selectedIds = new Set(
    (multi ? (value || []) : (value ? [value] : [])).map(v => String(v.id))
  );
  const visible = filter
    ? options.filter(([, name]) => name.toLowerCase().includes(filter.toLowerCase()))
    : options;

  if (!multi) {
    return (
      <select
        className="fmx-select"
        style={{ maxWidth: 340 }}
        value={value ? String(value.id) : ''}
        onChange={(e) => {
          const id = e.target.value;
          onChange(id ? { id: parseInt(id, 10), name: sf.options[id] } : null);
        }}
      >
        <option value="">{sf.isRequired ? 'Select…' : '(none)'}</option>
        {options.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
      </select>
    );
  }
  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: T.radiusBtn, maxWidth: 420 }}>
      {options.length > 8 && (
        <input
          className="fmx-input"
          style={{ width: '100%', border: 'none', borderBottom: `1px solid ${T.borderSubtle}`, borderRadius: 0 }}
          placeholder={`Filter ${options.length} options…`}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      )}
      <div style={{ maxHeight: 168, overflowY: 'auto', padding: '6px 10px' }}>
        {visible.length === 0 && <div style={{ font: `400 12px/18px ${T.fontSans}`, color: T.inkSoft }}>No matches.</div>}
        {visible.map(([id, name]) => (
          <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 7, font: `400 13px/22px ${T.fontSans}`, color: T.ink, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={selectedIds.has(id)}
              onChange={(e) => {
                const cur = value || [];
                onChange(e.target.checked
                  ? [...cur, { id: parseInt(id, 10), name }]
                  : cur.filter(v => String(v.id) !== id));
              }}
            />
            {name}
          </label>
        ))}
      </div>
      {selectedIds.size > 0 && (
        <div style={{ padding: '4px 10px', borderTop: `1px solid ${T.borderSubtle}`, font: `500 11px/16px ${T.fontSans}`, color: T.inkMuted }}>
          {selectedIds.size} selected — leaving all unchecked applies the field site-wide for this fieldset
        </div>
      )}
    </div>
  );
}

export default function ScopeStep({ project, session, stage1, suggestions, fetchPostOptionsForFieldset, onConfirm, onBack }) {
  const [selected, setSelected] = useState(() => new Set(suggestions.map(s => s.key)));
  const [postOptions, setPostOptions] = useState({});   // fieldset → { systemFields }
  const [loadErr, setLoadErr] = useState({});           // fieldset → message
  const [scoping, setScoping] = useState({});           // fieldset → { key: value }
  const [sectionRequestTypes, setSectionRequestTypes] = useState({}); // section title → {id,name}
  const [confirming, setConfirming] = useState(false);

  const orderedSelected = useMemo(
    () => ALL_FIELDSETS.map(f => f.key).filter(k => selected.has(k)),
    [selected]
  );

  // Fetch live post-options lazily per selected fieldset — it is required
  // before stage 2 can run against that fieldset.
  useEffect(() => {
    for (const fs of orderedSelected) {
      if (postOptions[fs] || loadErr[fs]) continue;
      fetchPostOptionsForFieldset(fs)
        .then(po => setPostOptions(prev => ({ ...prev, [fs]: po })))
        .catch(e => setLoadErr(prev => ({ ...prev, [fs]: e?.message || String(e) })));
    }
  }, [orderedSelected]); // dep: fetch once per newly-selected fieldset; postOptions/loadErr guard re-entry

  const itemCount = (stage1?.sections || []).reduce((n, s) => n + (s.items?.length || 0), 0);

  // Titled sections are the candidate request types (a form document's
  // ALL-CAPS headers), and the live request-type map comes from whichever
  // confirmed fieldset carries requestTypeIDs.
  const sectionTitles = useMemo(
    () => [...new Set((stage1?.sections || []).map(s => s.title).filter(Boolean))],
    [stage1]
  );
  const requestTypeOptions = useMemo(() => {
    for (const fs of orderedSelected) {
      const sf = (postOptions[fs]?.systemFields || []).find(x => x.key === 'requestTypeIDs');
      if (sf?.options) {
        return Object.entries(sf.options).map(([id, name]) => ({ id: parseInt(id, 10), name }));
      }
    }
    return [];
  }, [orderedSelected, postOptions]);

  // A gentle proposal, never applied silently: a section matches a request
  // type when their normalized names agree or one contains the other
  // ("SCHOOL EVENT" → "School Event").
  const suggestedSectionMatch = (title) => {
    const norm = normalizeFieldName(title);
    if (!norm) return null;
    return requestTypeOptions.find(o => {
      const other = normalizeFieldName(o.name);
      return other && (other === norm || norm.includes(other) || other.includes(norm));
    }) || null;
  };

  const missingRequired = orderedSelected.some(fs => {
    const po = postOptions[fs];
    if (!po) return true; // still loading
    return (po.systemFields || []).some(sf =>
      isScopingKey(sf.key) && sf.isRequired &&
      !(isArrayKey(sf.key) ? (scoping[fs]?.[sf.key] || []).length : scoping[fs]?.[sf.key])
    );
  });

  const confirm = async () => {
    setConfirming(true);
    await onConfirm(
      { fieldsets: orderedSelected, scoping, sectionRequestTypes },
      Object.fromEntries(orderedSelected.map(fs => [fs, postOptions[fs]]))
    );
    setConfirming(false);
  };

  return (
    <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, padding: 24 }}>
      <div style={{ font: `700 18px/24px ${T.fontSans}`, color: T.ink, marginBottom: 3 }}>Confirm the scope</div>
      <div style={{ font: `400 13px/19px ${T.fontSans}`, color: T.inkMuted, marginBottom: 16 }}>
        Read {itemCount} items from “{stage1?.formTitle || session?.source_filename || 'the document'}”.
        Pick which FMX form(s) these fields belong to. Fieldset and module scope
        can’t be changed after a field is created in FMX, so they are confirmed here first.
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '9px 12px', background: T.blue50, borderRadius: T.radiusBtn, marginBottom: 16 }}>
        <i className="fa-solid fa-server" style={{ color: T.brand, fontSize: 12 }} />
        <span style={{ font: `500 13px/18px ${T.fontSans}`, color: T.ink }}>
          Target site: <b>{project?.fmx_site_url || '—'}</b>
        </span>
        <span style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkSoft }}>(this project’s saved FMX site)</span>
      </div>

      {suggestions.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ font: `700 12px/16px ${T.fontSans}`, color: T.inkMuted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            Suggested from the document
          </div>
          {suggestions.map(s => (
            <div key={s.key} style={{ font: `400 13px/20px ${T.fontSans}`, color: T.ink }}>
              <Badge color="blueLight">{fieldsetLabel(s.key)}</Badge>
              <span style={{ color: T.inkMuted, marginLeft: 8 }}>{s.why}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '4px 16px', marginBottom: 6 }}>
        {FIELDSET_GROUPS.map(g => (
          <div key={g.group} style={{ marginBottom: 10 }}>
            <div style={{ font: `700 12px/16px ${T.fontSans}`, color: T.inkMuted, marginBottom: 4 }}>{g.group}</div>
            {g.fieldsets.map(f => (
              <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 7, font: `400 13px/22px ${T.fontSans}`, color: T.ink, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selected.has(f.key)}
                  onChange={(e) => setSelected(prev => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(f.key); else next.delete(f.key);
                    return next;
                  })}
                />
                {f.label}
              </label>
            ))}
          </div>
        ))}
      </div>

      {orderedSelected.map(fs => {
        const po = postOptions[fs];
        const err = loadErr[fs];
        const scopingFields = (po?.systemFields || []).filter(sf => isScopingKey(sf.key) && sf.options && Object.keys(sf.options).length > 0);
        return (
          <div key={fs} style={{ border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, padding: '12px 14px', marginBottom: 10 }}>
            <div style={{ font: `700 14px/20px ${T.fontSans}`, color: T.ink, marginBottom: 6 }}>
              {fieldsetLabel(fs)}
              <span style={{ font: `400 12px/17px ${T.fontData}`, color: T.inkSoft, marginLeft: 8 }}>{fs}</span>
            </div>
            {err && <div style={{ font: `500 12px/17px ${T.fontSans}`, color: T.red500 }}>Could not load live options: {err}</div>}
            {!po && !err && <div style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkSoft }}>Loading live options from FMX…</div>}
            {po && scopingFields.length === 0 && (
              <div style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkSoft }}>No scoping needed — fields apply site-wide for this fieldset.</div>
            )}
            {scopingFields.map(sf => (
              <div key={sf.key} style={{ margin: '8px 0' }}>
                <div style={{ font: `600 12px/17px ${T.fontSans}`, color: T.ink, marginBottom: 3 }}>
                  {sf.label || sf.key}
                  {sf.isRequired && <span style={{ color: T.red500 }}> *</span>}
                </div>
                <ScopingPicker
                  sf={sf}
                  value={scoping[fs]?.[sf.key]}
                  onChange={(v) => setScoping(prev => ({ ...prev, [fs]: { ...(prev[fs] || {}), [sf.key]: v } }))}
                />
              </div>
            ))}
          </div>
        );
      })}

      {/* Section → request type (rule R2). A document with a section per
          request type repeats the same questions in each; matching sections to
          live request types here is what lets ONE merged field be created
          scoped to exactly the request types that asked it. */}
      {sectionTitles.length > 1 && requestTypeOptions.length > 0 && (
        <div style={{ border: `1px solid ${T.badge.blueLight}`, background: T.tint.info, borderRadius: T.radiusCard, padding: '12px 14px', marginBottom: 10 }}>
          <div style={{ font: `700 14px/20px ${T.fontSans}`, color: T.brand, marginBottom: 3 }}>
            <i className="fa-solid fa-diagram-project" /> This document looks like one form per request type
          </div>
          <div style={{ font: `400 12px/18px ${T.fontSans}`, color: T.inkMuted, marginBottom: 8, maxWidth: 680 }}>
            It has {sectionTitles.length} sections. Match each to a request type on your site and the
            same question asked in several sections becomes <b>one</b> custom field, scoped to just
            those request types — instead of one duplicate per section. Leave a section unmatched to
            skip that scoping.
          </div>
          {sectionTitles.map(title => (
            <div key={title} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
              <span style={{ font: `500 12px/17px ${T.fontSans}`, color: T.ink, width: 260, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {title}
              </span>
              <i className="fa-solid fa-arrow-right" style={{ fontSize: 9, color: T.inkSoft }} />
              <select
                className="fmx-select"
                style={{ width: 'auto', minWidth: 220, maxWidth: 320 }}
                value={sectionRequestTypes[title]?.id ?? ''}
                onChange={(e) => {
                  const id = e.target.value;
                  setSectionRequestTypes(prev => {
                    const next = { ...prev };
                    if (!id) delete next[title];
                    else next[title] = { id: parseInt(id, 10), name: requestTypeOptions.find(o => String(o.id) === id)?.name };
                    return next;
                  });
                }}
              >
                <option value="">(not a request type / skip)</option>
                {requestTypeOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
              {!sectionRequestTypes[title] && suggestedSectionMatch(title) && (
                <button
                  className="fmx-btn-xs"
                  onClick={() => setSectionRequestTypes(prev => ({ ...prev, [title]: suggestedSectionMatch(title) }))}
                >
                  Use “{suggestedSectionMatch(title).name}”
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
        <button className="fmx-btn-nav-back" onClick={onBack}>Back to sessions</button>
        <Button
          variant="primary" iconAfter="arrow-right"
          disabled={orderedSelected.length === 0 || missingRequired || confirming}
          onClick={confirm}
        >
          {confirming ? 'Mapping…' : 'Confirm scope & map to FMX'}
        </Button>
      </div>
    </div>
  );
}
